/**
 * PrivacyPolicyPage.jsx
 * 
 * Full privacy policy page for Touchstone.
 */

import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Footer from './Footer';

export default function PrivacyPolicyPage() {
  const navigate = useNavigate();

  return (
    <div className="fixed inset-0 overflow-y-auto bg-[#030303] select-text">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#030303]/80 backdrop-blur-md border-b border-white/[0.04]">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link
            to="/home"
            className="text-xl text-white"
            style={{ fontFamily: "'Qulio Display', 'Bodoni Moda', serif", letterSpacing: '0.05em' }}
          >
            TOUCHSTONE
          </Link>
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        </div>
      </header>

      {/* Content */}
      <div className="pt-36 pb-16 px-6">
        <div className="max-w-3xl mx-auto">
          <h1
            className="text-4xl md:text-5xl text-white mb-4"
            style={{ fontFamily: "'Qulio Display', 'Bodoni Moda', serif" }}
          >
            Privacy Policy
          </h1>
          <p className="text-sm text-zinc-500 mb-16">Last updated: February 16, 2026</p>

          <div className="space-y-12 text-zinc-400 leading-relaxed">
            
            <section>
              <h2 className="text-xl text-white font-medium mb-4">1. Introduction</h2>
              <p>
                Touchstone ("we," "our," or "us") is a neutral research platform that documents real-world AI tool usage. 
                We are committed to protecting your privacy and being transparent about how we collect, use, and share information. 
                This Privacy Policy explains our practices when you visit or use our website and services.
              </p>
            </section>

            <section>
              <h2 className="text-xl text-white font-medium mb-4">2. Information We Collect</h2>
              <h3 className="text-base text-zinc-300 font-medium mb-3">Information You Provide</h3>
              <ul className="list-disc list-inside space-y-2 mb-6 ml-2">
                <li>Profile preferences (role, tools you use, priorities) — stored locally in your browser</li>
                <li>Workflow configurations you create and save</li>
                <li>Contact information if you reach out to us directly</li>
              </ul>
              <h3 className="text-base text-zinc-300 font-medium mb-3">Information Collected Automatically</h3>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>Usage data such as pages visited, features used, and time spent on the platform</li>
                <li>Device and browser information (type, operating system, screen resolution)</li>
                <li>IP address and approximate geographic location</li>
                <li>Referral source (how you arrived at our site)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl text-white font-medium mb-4">3. How We Use Your Information</h2>
              <p className="mb-4">We use the information we collect to:</p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>Provide, maintain, and improve the Touchstone platform</li>
                <li>Personalize your experience (e.g., workflow recommendations based on your profile)</li>
                <li>Analyze usage patterns to improve our content and features</li>
                <li>Respond to your inquiries and provide support</li>
                <li>Detect and prevent technical issues or abuse</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl text-white font-medium mb-4">4. Data Storage</h2>
              <p>
                Your profile preferences, saved workflows, and configuration data are stored locally in your browser 
                using localStorage. This data never leaves your device unless you explicitly choose to share it. 
                We do not maintain user accounts or store personal profile data on our servers.
              </p>
            </section>

            <section>
              <h2 className="text-xl text-white font-medium mb-4">5. Cookies & Tracking</h2>
              <p className="mb-4">
                We use minimal, essential cookies and similar technologies to ensure the platform functions correctly. 
                We may use privacy-respecting analytics to understand aggregate usage patterns.
              </p>
              <p>
                We do <span className="text-white">not</span> use advertising cookies, tracking pixels from ad networks, 
                or any third-party tools that build advertising profiles. Touchstone has no affiliate links, 
                no sponsored content, and no ad-driven tracking.
              </p>
            </section>

            <section>
              <h2 className="text-xl text-white font-medium mb-4">6. Third-Party Sharing</h2>
              <p>
                We do not sell, rent, or share your personal information with third parties for marketing purposes. 
                We may share anonymized, aggregated usage statistics that cannot identify individual users. 
                If required by law, we may disclose information in response to valid legal requests.
              </p>
            </section>

            <section>
              <h2 className="text-xl text-white font-medium mb-4">7. Your Rights</h2>
              <p className="mb-4">You have the right to:</p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>Access, correct, or delete your locally stored data by clearing your browser's localStorage</li>
                <li>Opt out of analytics tracking (where applicable)</li>
                <li>Request information about what data we hold, if any, that relates to you</li>
                <li>Lodge a complaint with a data protection authority if you believe your rights have been violated</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl text-white font-medium mb-4">8. Data Security</h2>
              <p>
                We implement reasonable technical and organizational measures to protect your information. 
                Our platform is served over HTTPS, and we regularly review our security practices. 
                However, no method of electronic transmission or storage is 100% secure.
              </p>
            </section>

            <section>
              <h2 className="text-xl text-white font-medium mb-4">9. Children's Privacy</h2>
              <p>
                Touchstone is not directed at children under the age of 13. We do not knowingly collect 
                personal information from children. If you believe a child has provided us with personal 
                information, please contact us so we can take appropriate action.
              </p>
            </section>

            <section>
              <h2 className="text-xl text-white font-medium mb-4">10. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. When we make material changes, 
                we will update the "Last updated" date at the top of this page. We encourage you to 
                review this policy periodically.
              </p>
            </section>

            <section>
              <h2 className="text-xl text-white font-medium mb-4">11. Contact Us</h2>
              <p>
                If you have questions about this Privacy Policy or our data practices, you can reach us at{' '}
                <a href="mailto:privacy@touchstone.dev" className="text-white hover:underline">
                  privacy@touchstone.dev
                </a>.
              </p>
            </section>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
